import { ApiError, meta } from "@baton/api";
import { useI18n } from "@baton/i18n";
import { toast } from "@baton/ui";
import { useEffect, useState } from "react";
import { meQuery, type MeView } from "../../app/queries.ts";
import type { Runtime } from "@baton/core";

/** Whether a signed-in person's address still needs proving: a person's, since an agent has none. */
export function isUnverified(me: MeView | undefined): me is MeView {
  return me?.kind === "user" && (me.email_verified_at ?? null) === null;
}

export interface EmailVerification {
  /** The address is not verified, so the send is offered. */
  readonly unverified: boolean;
  readonly sending: boolean;
  readonly send: () => Promise<void>;
}

/**
 * 001 FR-153 (2026-09-28): the person's own verification link, sent from the profile and from the
 * banner. A send answers "Sent. Check your inbox"; a 409 means the address was verified meanwhile,
 * so `/me` is read again and the offer goes; anything else is a failure toast with the problem's
 * words, a 429 among them. While the address is unverified, `/me` is read again whenever the window
 * regains focus, which is the moment a person comes back from their mail client, so a verification
 * made in another tab or on another device shows here without a reload.
 */
export function useEmailVerification(runtime: Runtime, me: MeView | undefined): EmailVerification {
  const { t } = useI18n();
  const [sending, setSending] = useState(false);
  const unverified = isUnverified(me);

  useEffect(() => {
    if (!unverified) return undefined;
    return runtime.platform.focus.subscribe(() => {
      void runtime.queryClient.invalidateQueries({ queryKey: meQuery(runtime).queryKey });
    });
  }, [runtime, unverified]);

  const send = async () => {
    setSending(true);
    try {
      // A person's own click: a 429 is said at once rather than retried after its wait.
      await meta.sendEmailVerification(runtime.signedIn, { retry: false });
      toast({ variant: "success", title: t("auth.verify.sent") });
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        toast({ variant: "success", title: t("auth.verify.alreadyVerified") });
        await runtime.queryClient.invalidateQueries({ queryKey: meQuery(runtime).queryKey });
      } else {
        const problem = error instanceof ApiError ? error.problem : null;
        toast({ variant: "error", title: t("auth.verify.sendFailed"), ...(problem === null ? { description: t("auth.verify.unreachable") } : { description: problem.detail ?? problem.title, requestId: problem.request_id }) });
      }
    } finally {
      setSending(false);
    }
  };

  return { unverified, sending, send };
}
